import posthog from 'posthog-js';
import { redactPostHogEvent } from './redactUrlSecrets';

const POSTHOG_KEY = import.meta.env.REACT_APP_POSTHOG_PROJECT_TOKEN as string | undefined;

export function initPostHog() {
  if (!POSTHOG_KEY) return;
  posthog.init(POSTHOG_KEY, {
    // Long-term: use a separate PostHog project key per environment.
    api_host: '/ingest',
    ui_host: 'https://eu.posthog.com',
    persistence: 'localStorage',
    autocapture: true,
    capture_pageview: false,
    capture_pageleave: false,
    capture_exceptions: true,
    capture_dead_clicks: true,
    // Jetons de reset/vérification et ticket OAuth retirés des URLs avant
    // envoi (audit sécurité 2026-10-22, H5).
    before_send: (event) => redactPostHogEvent(event),
  });

  const hostname = window.location.hostname;
  const environment = hostname.includes('preprod')
    ? 'preprod'
    : hostname === 'localhost' || hostname === '127.0.0.1'
      ? 'development'
      : 'production';
  posthog.register({ environment });
}

export function identifyUser(user: { id: string; email?: string; subscriptionPlan?: string; subscriptionStatus?: string; createdAt?: Date }) {
  if (!POSTHOG_KEY) return;
  posthog.identify(user.id, {
    email: user.email,
    plan: user.subscriptionPlan,
    plan_status: user.subscriptionStatus,
    created_at: user.createdAt,
  });
}

export function resetPostHog() {
  if (!POSTHOG_KEY) return;
  posthog.reset();
}

export function trackPageview(path: string) {
  if (!POSTHOG_KEY) return;
  posthog.capture('$pageview', { path });
}

export function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  posthog.capture(event, properties);
}

export function captureException(error: unknown, context?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  const err = error instanceof Error ? error : new Error(String(error));
  posthog.captureException(err, context);
}

// Auth
export const trackLogin = () => trackEvent('user_logged_in');

// Trades
export const trackTradeDeleted = () => trackEvent('trade_deleted');

// Sessions
export const trackSessionStarted = () => trackEvent('session_started');
export const trackSessionEnded = (props: { durationMinutes: number; mood?: string; rating?: string }) =>
  trackEvent('session_ended', { duration_minutes: props.durationMinutes, mood: props.mood, rating: props.rating });
export const trackSessionShared = () => trackEvent('session_shared');

// Widgets & layout
export const trackWidgetAdded = (widgetKey: string) => trackEvent('widget_added', { widget_key: widgetKey });

/**
 * Two-level navigation usage. Logged on every module/tab change so we can measure,
 * after release, how long it takes to reach the most-visited pages.
 */
export const trackNavigation = (props: { module: string; tab: string; path: string }) =>
  trackEvent('nav_navigated', {
    nav_module: props.module,
    nav_tab: props.tab,
    nav_path: props.path,
  });
export const trackWidgetRemoved = (widgetKey: string) => trackEvent('widget_removed', { widget_key: widgetKey });
export const trackLayoutSaved = () => trackEvent('layout_saved');
export const trackLayoutShared = () => trackEvent('layout_shared');

// Prop firms
export const trackPayoutAdded = () => trackEvent('payout_added');
export const trackAccountCreated = (type: string) => trackEvent('account_created', { type });

// Notebook
export const trackNoteCreated = () => trackEvent('note_created');
export const trackNoteDeleted = () => trackEvent('note_deleted');

// Discipline
export const trackRoutineCreated = () => trackEvent('routine_created');
export const trackGoalCreated = () => trackEvent('goal_created');
export const trackRoutineCompleted = () => trackEvent('routine_completed');

// Custom pages
export const trackCustomPageCreated = () => trackEvent('custom_page_created');
export const trackCustomPageDeleted = () => trackEvent('custom_page_deleted');

// Social reports
export const trackSocialReportGenerated = (type: string) => trackEvent('social_report_generated', { type });

// Onboarding — mêmes événements pour les deux parcours, distingués par `mode`.
// Advanced : modale 4 étapes (OnboardingModal). Lite : 3 écrans (LiteOnboarding)
// puis la checklist « Bien démarrer » du dashboard (GettingStarted).
export type OnboardingMode = 'advanced' | 'lite';
export const trackOnboardingShown = (mode: OnboardingMode, step: string) =>
  trackEvent('onboarding_shown', { mode, step });
export const trackOnboardingStepViewed = (mode: OnboardingMode, step: string) =>
  trackEvent('onboarding_step_viewed', { mode, step });
export const trackOnboardingStepCompleted = (mode: OnboardingMode, step: string, props?: Record<string, unknown>) =>
  trackEvent('onboarding_step_completed', { mode, step, ...props });
export const trackOnboardingCtaClicked = (mode: OnboardingMode, step: string, props?: Record<string, unknown>) =>
  trackEvent('onboarding_cta_clicked', { mode, step, ...props });
export const trackOnboardingCompleted = (mode: OnboardingMode, props?: Record<string, unknown>) =>
  trackEvent('onboarding_completed', { mode, ...props });
export const trackOnboardingSkipped = (mode: OnboardingMode, step: string) =>
  trackEvent('onboarding_skipped', { mode, step });
export const trackOnboardingChecklistHidden = (done: number, total: number) =>
  trackEvent('onboarding_checklist_hidden', { mode: 'lite', done, total });

// Payment
export const trackUpgradeStarted = (planId: string) => trackEvent('upgrade_started', { plan_id: planId });
export const trackUpgradeCompleted = (planId: string, price: number) =>
  trackEvent('upgrade_completed', { plan_id: planId, price });

// MetaAPI
export const trackMetaApiConnected = () => trackEvent('metaapi_connected');

// Trades (compléments)
export const trackBulkTradeDeleted = (count: number) =>
  trackEvent('bulk_trade_deleted', { count });
export const trackCsvExported = (count: number) =>
  trackEvent('csv_exported', { count });

// Accounts (compléments)
export const trackAccountGroupCreated = () =>
  trackEvent('account_group_created');

// Mentor
export const trackInvitationSent = (type: 'single' | 'bulk', count?: number) =>
  trackEvent('mentor_invitation_sent', { type, count });
export const trackStudentViewed = (studentId: string) =>
  trackEvent('mentor_student_viewed', { student_id: studentId });

// Landing page
export const trackLandingCta = (
  location: 'hero' | 'pricing' | 'cta_final' | 'comparison_table' | 'footer',
  plan?: 'free' | 'pro' | 'max'
) => trackEvent('landing_cta_clicked', { location, plan });

export const trackPricingViewed = () => trackEvent('pricing_viewed');
